import { getDb } from '@/data/db';
import type { MediaQueueItem } from '@/types/domain';
import { isVerifiableField, isSignatureMedia } from '@/types/domain';
import { clientId } from '@/lib/utils';

const MAX_ATTEMPTS = 5;
const RETENTION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export const mediaQueueRepo = {
  async enqueue(
    item: Omit<MediaQueueItem, 'id' | 'attempts' | 'status'>,
  ): Promise<MediaQueueItem> {
    const row: MediaQueueItem = {
      ...item,
      id: clientId(),
      attempts: 0,
      status: 'pending',
    };
    await getDb().mediaQueue.add(row);
    return row;
  },

  async pending(limit = 5): Promise<MediaQueueItem[]> {
    return getDb()
      .mediaQueue.where('status')
      .anyOf(['pending', 'inflight'])
      .limit(limit)
      .toArray();
  },

  async resolveFmRecordId(id: string, fmRecordId: string): Promise<void> {
    await getDb().mediaQueue.update(id, { fmRecordId });
  },

  async markInflight(id: string): Promise<void> {
    await getDb().mediaQueue.update(id, { status: 'inflight' });
  },

  /** Persist a corrected container field name (see repairMediaFieldName). */
  async setFieldName(id: string, fieldName: string): Promise<void> {
    await getDb().mediaQueue.update(id, { fieldName });
  },

  async markUploaded(id: string): Promise<void> {
    await getDb().mediaQueue.update(id, {
      status: 'uploaded',
      uploadedAt: new Date().toISOString(),
      lastError: undefined,
    });
  },

  async markFailed(id: string, error: string): Promise<void> {
    const row = await getDb().mediaQueue.get(id);
    if (!row) return;
    const attempts = row.attempts + 1;
    await getDb().mediaQueue.update(id, {
      attempts,
      lastError: error,
      status: attempts >= MAX_ATTEMPTS ? 'quarantined' : 'pending',
    });
  },

  /**
   * Permanently fail an item (no more retries). Used when the payload can no
   * longer be recovered from storage, so retrying would only re-post an empty
   * file. The driver is prompted to re-capture instead.
   */
  async quarantine(id: string, error: string): Promise<void> {
    await getDb().mediaQueue.update(id, {
      status: 'quarantined',
      lastError: error,
    });
  },

  async getAll(): Promise<MediaQueueItem[]> {
    return getDb().mediaQueue.orderBy('capturedAt').reverse().toArray();
  },

  /** Count of media still awaiting upload (pending/inflight). */
  async pendingCount(): Promise<number> {
    return getDb().mediaQueue.where('status').anyOf(['pending', 'inflight']).count();
  },

  /** Permanently failed media (needs recovery/re-capture); heartbeat metric. */
  async quarantinedCount(): Promise<number> {
    return getDb().mediaQueue.where('status').equals('quarantined').count();
  },

  async getQuarantined(): Promise<MediaQueueItem[]> {
    return getDb().mediaQueue.where('status').equals('quarantined').toArray();
  },

  async get(id: string): Promise<MediaQueueItem | undefined> {
    return getDb().mediaQueue.get(id);
  },

  /**
   * Retained media search (POD insurance). Mirrors native `findRetained`:
   * optional PRO (tag) substring filter and a signatures/PODs-only filter.
   * Returns newest first.
   */
  async findRetained(opts: { tag?: string; signaturesOnly?: boolean } = {}): Promise<
    MediaQueueItem[]
  > {
    const tag = (opts.tag ?? '').trim().toLowerCase();
    const all = await this.getAll();
    return all.filter((m) => {
      if (opts.signaturesOnly && !isSignatureMedia(m)) return false;
      if (tag && !(m.tag ?? '').toLowerCase().includes(tag)) return false;
      return true;
    });
  },

  /**
   * Uploaded but not yet verified end-to-end (verifiable artifacts only).
   */
  async getUnverified(): Promise<MediaQueueItem[]> {
    const uploaded = await getDb().mediaQueue.where('status').equals('uploaded').toArray();
    return uploaded.filter((m) => isVerifiableField(m.fieldName) && !m.verified);
  },

  async markVerified(id: string): Promise<void> {
    await getDb().mediaQueue.update(id, {
      verified: true,
      verifiedAt: new Date().toISOString(),
    });
  },

  /**
   * Re-queue an uploaded/quarantined item for another upload attempt
   * (driver-initiated recovery). Clears verification so the clock restarts.
   */
  async requeueForResend(id: string): Promise<void> {
    await getDb().mediaQueue.update(id, {
      status: 'pending',
      attempts: 0,
      uploadedAt: null,
      verified: false,
      verifiedAt: null,
      lastError: undefined,
    });
  },

  /**
   * 30-day retention:
   *  - verifiable artifacts (signatures, BOL/POD masters): purge 30 days AFTER
   *    end-to-end verification. Never purge while still unverified.
   *  - non-verifiable photos: purge 30 days after upload.
   */
  async purgeExpired(): Promise<number> {
    const now = Date.now();
    const all = await getDb().mediaQueue.where('status').equals('uploaded').toArray();
    const toDelete: string[] = [];
    for (const m of all) {
      if (isVerifiableField(m.fieldName)) {
        if (m.verified && m.verifiedAt && now - Date.parse(m.verifiedAt) > RETENTION_MS) {
          toDelete.push(m.id);
        }
      } else if (m.uploadedAt && now - Date.parse(m.uploadedAt) > RETENTION_MS) {
        toDelete.push(m.id);
      }
    }
    if (toDelete.length) await getDb().mediaQueue.bulkDelete(toDelete);
    return toDelete.length;
  },

  /**
   * Verifiable uploads still unverified after a stale threshold (default 6h);
   * surfaced on the recovery screen and, with identifiers, in the heartbeat.
   */
  async staleUnverified(thresholdMs = 6 * 60 * 60 * 1000): Promise<MediaQueueItem[]> {
    const now = Date.now();
    const unverified = await this.getUnverified();
    return unverified.filter(
      (m) => m.uploadedAt && now - Date.parse(m.uploadedAt) > thresholdMs,
    );
  },

  async staleUnverifiedCount(thresholdMs = 6 * 60 * 60 * 1000): Promise<number> {
    return (await this.staleUnverified(thresholdMs)).length;
  },

  /** Pending/inflight media that has already failed at least once (carries a lastError). */
  async getPendingWithErrors(): Promise<MediaQueueItem[]> {
    return getDb()
      .mediaQueue.where('status')
      .anyOf(['pending', 'inflight'])
      .filter((r) => !!r.lastError)
      .toArray();
  },
};
